import { Injectable } from '@angular/core';
import { AumicoCoreProvider, NotificationType } from '@modeso/aumico-lib-core-fe';
import { ExportTasksFilterState, ExportTasksSectionExcelData, TaskState } from '@modeso/aumico-lib-cpmm-fe';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { DocumentsActions } from '../actions/workflow-documents.actions';
import { ITasksExportTableHeaders } from '../dtos/tasks-export-table-headers.dto';
import { WorkflowDocumentsService } from '../services/workflow-documents.service';
import { GetOrganizationDto } from 'projects/aumico-lib-users-fe/src/public-api';

@Injectable()
export class WorkflowDocumentsEffects {
    private openedFileUrls = new Map<string, { url: string; blob: Blob }>();
    constructor(
        private actions$: Actions,
        private service: WorkflowDocumentsService,
        private coreProvider: AumicoCoreProvider,
    ) { }

    loadTaskDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.loadTaskDocuments),
            switchMap(({ taskId }) =>
                this.service.loadDocuments$(taskId).pipe(
                    map((documents) => DocumentsActions.loadTaskDocumentsSuccess({ documents })),
                    catchError((error) => of(DocumentsActions.loadTaskDocumentsFailure({ error }))),
                ),
            ),
        ),
    );

    // Check preview status
    checkPreviewStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.checkPreviewStatus),
            mergeMap(({ fileId }) =>
                this.service.getPreviewStatus(fileId).pipe(
                    map(({ status }) => DocumentsActions.checkPreviewStatusSuccess({ fileId, status })),
                    catchError((error) => of(DocumentsActions.checkPreviewStatusFailure({ fileId, error }))),
                ),
            ),
        ),
    );

    // Request preview generation
    requestPreviewGeneration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.requestPreviewGeneration),
            mergeMap(({ fileId }) =>
                this.service.requestPreviewGeneration(fileId).pipe(
                    map(({ requestId }) => DocumentsActions.requestPreviewGenerationSuccess({ fileId, requestId })),
                    catchError((error) => of(DocumentsActions.requestPreviewGenerationFailure({ fileId, error }))),
                ),
            ),
        ),
    );

    // Load generated preview URL
    loadPreviewUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.loadPreviewUrl),
            mergeMap(({ fileId }) =>
                this.service.getPreview(fileId).pipe(
                    map(({ url }) => DocumentsActions.loadPreviewUrlSuccess({ fileId, url })),
                    catchError((error) => of(DocumentsActions.loadPreviewUrlFailure({ fileId, error }))),
                ),
            ),
        ),
    );

    openFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.openFile),
            switchMap(({ fileId }) => {
                return this.service.getFileContent(fileId).pipe(
                    map((blob) => {
                        const url = URL.createObjectURL(blob);
                        this.openedFileUrls.set(fileId, { url, blob });
                        return DocumentsActions.openFileSuccess({ fileId, url });
                    }),
                    catchError((error) => {
                        return of(DocumentsActions.openFileFailure({ fileId, error }));
                    }),
                );
            }),
        ),
    );

    revokeFileUrl$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DocumentsActions.revokeFileUrl),
                tap(({ fileId }) => {
                    const fileData = this.openedFileUrls.get(fileId);
                    if (fileData) {
                        URL.revokeObjectURL(fileData.url);
                        this.openedFileUrls.delete(fileId);
                    }
                }),
            ),
        { dispatch: false },
    );

    uploadDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.uploadDocument),
            switchMap(({ taskId, files, document }) =>
                this.service.uploadDocument$(taskId, files, document).pipe(
                    map((documents) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_update_success_title',
                            'aumico_cpmm_task_update_success_description',
                            NotificationType.SUCCESS,
                        );
                        return DocumentsActions.uploadDocumentSuccess({ documents });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_document_uploaded_failure_title',
                            'aumico_cpmm_document_uploaded_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(DocumentsActions.uploadDocumentFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    updateDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.updateDocumentMetadata),
            switchMap(({ document }) =>
                this.service.updateDocumentMetaData$(document).pipe(
                    map((document) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_task_update_success_title',
                            'aumico_cpmm_task_update_success_description',
                            NotificationType.SUCCESS,
                        );
                        return DocumentsActions.updateDocumentMetadataSuccess({ document });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_document_uploaded_failure_title',
                            'aumico_cpmm_document_uploaded_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(DocumentsActions.updateDocumentMetadataFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    exportProjectDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.exportProjectDocuments),
            switchMap(({ projectId, companyName, balanceSheetDate, workFlowAreas, inventoryData }) =>
                this.service.exportProjectDocuments$(projectId, companyName, balanceSheetDate, workFlowAreas, inventoryData).pipe(
                    map((blob) => DocumentsActions.exportProjectDocumentsSuccess({ blob })),
                    catchError((error) => of(DocumentsActions.exportProjectDocumentsFailure({ error }))),
                ),
            ),
        ),
    );

    deleteDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.deleteDocument),
            switchMap(({ documentId, projectId }) =>
                this.service.deleteDocument$(documentId, projectId).pipe(
                    map(() => DocumentsActions.deleteDocumentSuccess({ documentId })),
                    catchError((error) => of(DocumentsActions.deleteDocumentFailure({ error }))),
                ),
            ),
        ),
    );

    exportTasksDataAsExcel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentsActions.exportTasksExcel.type),
            mergeMap(
                (action: {
                    type: string;
                    payload: {
                        tableHeaders: ITasksExportTableHeaders;
                        data: ExportTasksSectionExcelData[];
                        fileName: string;
                        tasksFilter: (TaskState | ExportTasksFilterState)[];
                        tableTitle: string;
                        organization: GetOrganizationDto;
                    };
                }) => {
                    return this.service.exportTasksDataAsExcel$(action.payload).pipe(
                        map((response) => {
                            const blob = new Blob([response as BlobPart], {
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            });
                            const downloadLink = document.createElement('a');
                            downloadLink.href = window.URL.createObjectURL(blob);
                            downloadLink.download = action.payload.fileName;
                            downloadLink.click();
                            window.URL.revokeObjectURL(downloadLink.href);
                            return DocumentsActions.exportTasksExcelSuccess();
                        }),
                        catchError((error) => {
                            return of(
                                DocumentsActions.exportTasksExcelFailure({
                                    error: error,
                                }),
                            );
                        }),
                    );
                },
            ),
        ),
    );
}
